/**
 * Filtering and ordering of the resource catalogs (boards and databases) - the ONE rule set shared
 * by the home dashboard and the workspace page, so the same filters mean the same thing in both.
 * Filtering hides resources the server already lists for this viewer (it never reveals others);
 * sorting only orders what is shown. Pure and unit-tested.
 */
export type ResourceKind = 'board' | 'database';
export type TypeFilter = 'all' | ResourceKind;
export type OwnerFilter = 'any' | 'mine' | 'others';
export type SpaceFilter = 'any' | 'personal' | 'team';
/** `manual`: the stored order (workspace); `opened`: by when the person opened it; `updated`: boards only. */
export type CatalogSort = 'manual' | 'opened' | 'updated' | 'name';

export interface CatalogFilters {
  type: TypeFilter;
  /** Only the person's own favorites (boards and databases). */
  favorites: boolean;
  /** Only what the person opened lately on this device (a filter - not the same as the sort). */
  recent: boolean;
  owner: OwnerFilter;
  /** The kind of workspace the resource lives in (the home dashboard only). */
  space: SpaceFilter;
  /** `null` = the default order of the page showing the catalog. */
  sort: CatalogSort | null;
}

export const DEFAULT_FILTERS: CatalogFilters = {
  type: 'all',
  favorites: false,
  recent: false,
  owner: 'any',
  space: 'any',
  sort: null,
};

/** What the filters look at - a neutral description of a board or a database. */
export interface Filterable {
  kind: ResourceKind;
  favorite: boolean;
  /** Opened lately by this person on this device. */
  recent: boolean;
  ownerId: string | null;
  space: 'personal' | 'team' | null;
  /** Lower-cased fields the search looks in (name first). */
  haystack: readonly string[];
}

/** Filters that differ from the default (the sort is not a filter and never counts). */
export function activeFilterCount(filters: CatalogFilters, offered?: Offered): number {
  const shows = (key: keyof Offered) => offered === undefined || offered[key];
  return [
    shows('type') && filters.type !== 'all',
    shows('favorites') && filters.favorites,
    shows('recent') && filters.recent,
    shows('owner') && filters.owner !== 'any',
    shows('space') && filters.space !== 'any',
  ].filter(Boolean).length;
}

/** Which filters a page offers (a workspace section is already a type; the dashboard has spaces). */
export interface Offered {
  type: boolean;
  favorites: boolean;
  recent: boolean;
  owner: boolean;
  space: boolean;
}

export function matchesFilters(
  item: Filterable,
  filters: CatalogFilters,
  viewerId: string | null,
  query: string,
  offered?: Offered,
): boolean {
  const shows = (key: keyof Offered) => offered === undefined || offered[key];
  if (shows('type') && filters.type !== 'all' && filters.type !== item.kind) return false;
  if (shows('favorites') && filters.favorites && !item.favorite) return false;
  if (shows('recent') && filters.recent && !item.recent) return false;
  if (shows('owner') && filters.owner !== 'any') {
    const mine = viewerId !== null && item.ownerId === viewerId;
    if (filters.owner === 'mine' ? !mine : mine) return false;
  }
  if (shows('space') && filters.space !== 'any' && item.space !== filters.space) return false;
  const needle = query.trim().toLocaleLowerCase();
  return needle === '' || item.haystack.some((field) => field.includes(needle));
}

const TYPES: readonly TypeFilter[] = ['all', 'board', 'database'];
const OWNERS: readonly OwnerFilter[] = ['any', 'mine', 'others'];
const SPACES: readonly SpaceFilter[] = ['any', 'personal', 'team'];
const SORTS: readonly CatalogSort[] = ['manual', 'opened', 'updated', 'name'];

const pick = <T extends string>(list: readonly T[], value: unknown, fallback: T): T =>
  list.find((item) => item === value) ?? fallback;

/** Stored preferences from an untrusted string: anything unknown falls back to the default. */
export function parseFilters(raw: string | null): CatalogFilters {
  try {
    const value: unknown = raw === null ? null : JSON.parse(raw);
    if (typeof value !== 'object' || value === null) return DEFAULT_FILTERS;
    const stored = value as Record<string, unknown>;
    return {
      type: pick(TYPES, stored.type, 'all'),
      favorites: stored.favorites === true,
      recent: stored.recent === true,
      owner: pick(OWNERS, stored.owner, 'any'),
      space: pick(SPACES, stored.space, 'any'),
      sort: SORTS.find((item) => item === stored.sort) ?? null,
    };
  } catch {
    return DEFAULT_FILTERS;
  }
}

/** The sort a page uses: the stored one if the page offers it, else the page's default. */
export function effectiveSort(
  filters: CatalogFilters,
  offered: readonly CatalogSort[],
  fallback: CatalogSort,
): CatalogSort {
  return filters.sort !== null && offered.includes(filters.sort) ? filters.sort : fallback;
}
